import { useState, useCallback, useEffect } from 'react';
import { calculateKalkulation, calculateUniversalOffer, generateKalkulationNumber, buildComparisonTable } from '@/utils/kalkulationEngine';
import { useSuppliers } from '@/hooks/useSuppliers';
import { apiFetch } from '@/lib/api';
import { ensureAufschlagConfig, loadAufschlagConfig, getAufschlagStatus } from '@/data/strukturaufschlag';
import { ensureNetzentgelte, ladeNetzentgelte, getNetzentgelteStatus } from '@/data/netzentgelte';

const INITIAL_FORM = {
  plz: '',
  verbrauch: '',
  zaehlverfahren: 'SLP',
  leistung: '',
  lieferbeginn: '',
  branche: '',
  spannungsebene: 'NS',
  kurzbezeichnung: '',
};

// Fehlertext aus einer Server-Antwort ziehen (ohne zu erfinden)
const errorFromResponse = async (resp, fallback) => {
  try {
    const body = await resp.json();
    if (body?.error) return body.error;
  } catch { /* Body ist kein JSON */ }
  return `${fallback} (HTTP ${resp.status})`;
};

export const useKalkulator = () => {
  const { suppliers, prices, loading: suppliersLoading } = useSuppliers();

  const [formData, setFormData] = useState(INITIAL_FORM);
  const [step, setStep] = useState(0); // 0=Eingabe, 1=Aufschlag, 2=Zusammenfassung
  const [provision, setProvision] = useState(1.0);
  const [result, setResult] = useState(null);
  const [comparisonTable, setComparisonTable] = useState(null);
  const [selectedSuppliers, setSelectedSuppliers] = useState(new Set());
  const [isCalculating, setIsCalculating] = useState(false);
  const [calcError, setCalcError] = useState(null);

  // Gespeicherte Kalkulationen kommen vom Server (kein localStorage mehr)
  const [savedKalkulationen, setSavedKalkulationen] = useState([]);
  const [kalkulationenLoading, setKalkulationenLoading] = useState(true);
  const [kalkulationenError, setKalkulationenError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Boersenbasierter Modus (Standard) vs. Versorgerpreise
  const [priceMode, setPriceMode] = useState('boerse');
  const [futures, setFutures] = useState(null);
  const [futuresError, setFuturesError] = useState(null);

  // Strukturaufschlag-Status (Server ist die Quelle der Wahrheit)
  const [aufschlagStatus, setAufschlagStatus] = useState(() => getAufschlagStatus());
  const [netzentgelteStatus, setNetzentgelteStatus] = useState(() => getNetzentgelteStatus());

  // ============================================================
  // Laden: Kalkulationen, Futures, Strukturaufschlag
  // ============================================================
  const fetchKalkulationen = useCallback(async () => {
    setKalkulationenLoading(true);
    try {
      const resp = await apiFetch('/api/kalkulationen');
      if (!resp.ok) {
        setKalkulationenError(await errorFromResponse(resp, 'Kalkulationen konnten nicht geladen werden'));
        setSavedKalkulationen([]);
        return;
      }
      const data = await resp.json();
      setSavedKalkulationen(Array.isArray(data) ? data : []);
      setKalkulationenError(null);
    } catch (err) {
      // Ehrlicher Fehlerzustand — keine veralteten Browser-Daten vortaeuschen
      setKalkulationenError('Server nicht erreichbar: ' + err.message);
      setSavedKalkulationen([]);
    } finally {
      setKalkulationenLoading(false);
    }
  }, []);

  useEffect(() => { fetchKalkulationen(); }, [fetchKalkulationen]);

  // Live-Futures beim Mount laden
  useEffect(() => {
    let active = true;
    apiFetch('/api/futures')
      .then(async (resp) => {
        if (!resp.ok) throw new Error(await errorFromResponse(resp, 'Boersendaten nicht verfuegbar'));
        return resp.json();
      })
      .then(data => {
        if (!active) return;
        if (!data || !data.prices || Object.keys(data.prices).length === 0) {
          setFutures(null);
          setFuturesError('Boersendaten unvollstaendig');
          return;
        }
        // Kurse, die die Quelle seit Wochen nicht fortgeschrieben hat, sehen
        // aus wie frische Kurse. Der Server meldet jetzt den echten Handelstag
        // mit; daraus wird hier eine sichtbare Sperre statt einer stillen
        // Hochrechnung auf veralteter Grundlage.
        setFutures(data);
        if (data.veraltet) {
          const tag = data.handelstag
            ? new Date(data.handelstag).toLocaleDateString('de-DE')
            : 'unbekannt';
          setFuturesError(
            `Die Boersendaten sind nicht aktuell: juengstes Settlement vom ${tag}`
            + (data.alter_tage != null ? ` (${data.alter_tage} Tage alt` : ' (')
            + `, zulaessig sind ${data.max_alter_tage} Tage). `
            + 'Aus diesen Kursen darf kein Angebot entstehen.'
          );
        } else {
          setFuturesError(null);
        }
      })
      .catch(err => {
        if (!active) return;
        setFutures(null);
        setFuturesError('Boersendaten konnten nicht geladen werden: ' + err.message);
      });
    return () => { active = false; };
  }, []);

  // Strukturaufschlag einmalig vom Server in den Modul-Cache laden.
  // Danach liest die Rechen-Engine ihn synchron aus diesem Cache.
  useEffect(() => {
    let active = true;
    ensureAufschlagConfig().then(() => {
      if (active) setAufschlagStatus(getAufschlagStatus());
    });
    return () => { active = false; };
  }, []);

  // Netzentgelt-Preisblaetter einmalig in den Modul-Cache laden — sonst rechnet
  // die Engine dauerhaft mit der regionalen Pauschale, obwohl Preisblaetter
  // gepflegt sind (und der RLM-Leistungspreis waere unerreichbar).
  useEffect(() => {
    let active = true;
    ensureNetzentgelte().then(() => {
      if (active) setNetzentgelteStatus(getNetzentgelteStatus());
    });
    return () => { active = false; };
  }, []);

  const reloadNetzentgelte = useCallback(async () => {
    await ladeNetzentgelte({ force: true });
    const status = getNetzentgelteStatus();
    setNetzentgelteStatus(status);
    return status;
  }, []);

  const reloadAufschlag = useCallback(async () => {
    await loadAufschlagConfig({ force: true });
    const status = getAufschlagStatus();
    setAufschlagStatus(status);
    return status;
  }, []);

  // Formular aktualisieren
  const updateForm = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  // Kernberechnung (geteilt von calculate + updateProvision)
  const runCalculation = useCallback((prov) => {
    if (priceMode === 'boerse') {
      return calculateUniversalOffer({
        plz: formData.plz,
        verbrauch: parseInt(formData.verbrauch),
        zaehlverfahren: formData.zaehlverfahren,
        spannungsebene: formData.spannungsebene,
        leistung: parseFloat(formData.leistung) || 0,
        // Der im Formular gewaehlte Netzbetreiber entscheidet, welches
        // Preisblatt gilt — bei 664 PLZ gibt es mehrere zur Auswahl.
        netzbetreiberName: formData.netzbetreiber || undefined,
        lieferbeginn: formData.lieferbeginn || undefined,
        provision: prov,
        futures: futures?.prices || {},
      });
    }
    return calculateKalkulation({
      plz: formData.plz,
      verbrauch: parseInt(formData.verbrauch),
      spannungsebene: formData.spannungsebene,
      zaehlverfahren: formData.zaehlverfahren,
      leistung: parseFloat(formData.leistung) || 0,
      netzbetreiberName: formData.netzbetreiber || undefined,
      lieferbeginn: formData.lieferbeginn || undefined,
      provision: prov,
      supplierPrices: prices,
      suppliers,
    });
  }, [priceMode, formData, futures, prices, suppliers]);

  // Berechnung starten
  const calculate = useCallback(async () => {
    if (!formData.plz || !formData.verbrauch) return null;

    setIsCalculating(true);
    setCalcError(null);

    try {
      if (priceMode === 'boerse') {
        // Der Boersenpreis braucht (a) Live-Futures und (b) den serverseitig
        // gepflegten Strukturaufschlag. Fehlt eines davon, wird NICHT still
        // mit erfundenen Werten gerechnet.
        const aufschlag = await ensureAufschlagConfig();
        setAufschlagStatus(getAufschlagStatus());
        if (!aufschlag.ok) {
          setCalcError(aufschlag.error || 'Strukturaufschlag konnte nicht geladen werden. Bitte erneut versuchen.');
          return null;
        }
        if (!futures?.prices) {
          setCalcError(futuresError || 'Boersendaten (EEX) sind nicht verfuegbar. Bitte Seite neu laden.');
          return null;
        }
        // Veraltete Kurse sind schlimmer als fehlende: sie sehen richtig aus.
        if (futures.veraltet) {
          setCalcError(futuresError || 'Die Boersendaten sind nicht aktuell genug fuer ein Angebot.');
          return null;
        }
      }

      const kalkulationResult = runCalculation(provision);

      setResult(kalkulationResult);
      setComparisonTable(buildComparisonTable(kalkulationResult));

      // Alle Versorger initial auswaehlen
      const allSupplierIds = new Set(kalkulationResult.results.map(r => r.supplier_id));
      setSelectedSuppliers(allSupplierIds);

      setStep(1); // Weiter zu Aufschlag

      // Nach oben scrollen
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);

      return kalkulationResult;
    } finally {
      setIsCalculating(false);
    }
  }, [formData, provision, priceMode, futures, futuresError, runCalculation]);

  // Provision aendern (Live-Update)
  const updateProvision = useCallback((newProvision) => {
    setProvision(newProvision);

    // Sofort neu berechnen wenn Ergebnis vorhanden
    if (result) {
      const updated = runCalculation(newProvision);
      setResult(updated);
      setComparisonTable(buildComparisonTable(updated));
    }
  }, [result, runCalculation]);

  // Versorger ein-/ausblenden
  const toggleSupplier = useCallback((supplierId) => {
    setSelectedSuppliers(prev => {
      const next = new Set(prev);
      if (next.has(supplierId)) {
        next.delete(supplierId);
      } else {
        next.add(supplierId);
      }
      return next;
    });
  }, []);

  // Gefilterte Ergebnisse (nur ausgewaehlte Versorger)
  const filteredResults = result?.results?.filter(r =>
    selectedSuppliers.has(r.supplier_id)
  ) || [];

  // ============================================================
  // Kalkulation speichern (serverseitig)
  // ============================================================
  const saveKalkulation = useCallback(async ({ leadId = null } = {}) => {
    if (!result) return null;

    setIsSaving(true);
    setSaveError(null);

    // Vollstaendiger Ergebnis-Schnappschuss, damit die Kalkulation spaeter
    // rekonstruierbar ist (nicht nur die Eingaben).
    const snapshot = {
      cheapestPerLaufzeit: result.cheapestPerLaufzeit,
      laufzeiten: result.laufzeiten,
      netzbetreiber: result.netzbetreiber,
      verbrauch: result.verbrauch,
      mode: result.mode,
      lieferbeginn: result.lieferbeginn,
      netzentgelt_ct_kwh: result.netzentgelt_ct_kwh,
      abgaben_ct_kwh: result.abgaben_ct_kwh,
      abgaben_detail: result.abgaben_detail,
      provision,
      form: { ...formData },
      price_mode: priceMode,
      futures_stand: futures?.stand || null,
      futures_quelle: futures?.quelle || null,
      result, // komplettes Ergebnis inkl. aller Versorger/Laufzeiten
    };

    const payload = {
      kalkulation_number: generateKalkulationNumber(),
      plz: formData.plz,
      verbrauch: parseInt(formData.verbrauch) || null,
      zaehlverfahren: formData.zaehlverfahren,
      leistung: parseFloat(formData.leistung) || null,
      lieferbeginn: formData.lieferbeginn || null,
      branche: formData.branche || null,
      spannungsebene: formData.spannungsebene,
      kurzbezeichnung: formData.kurzbezeichnung || null,
      price_mode: priceMode,
      provision,
      netzbetreiber: result.netzbetreiber,
      netzentgelt_ct_kwh: result.netzentgelt_ct_kwh,
      abgaben_ct_kwh: result.abgaben_ct_kwh,
      data: snapshot,
      lead_id: leadId || null,
    };

    try {
      const resp = await apiFetch('/api/kalkulationen', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      if (!resp.ok) {
        setSaveError(await errorFromResponse(resp, 'Kalkulation konnte nicht gespeichert werden'));
        return null;
      }
      const created = await resp.json();
      setSavedKalkulationen(prev => [created, ...prev]);
      return created;
    } catch (err) {
      setSaveError('Server nicht erreichbar: ' + err.message);
      return null;
    } finally {
      setIsSaving(false);
    }
  }, [result, formData, provision, priceMode, futures]);

  // Kalkulation laden (Server-IDs sind Ganzzahlen -> String-Vergleich)
  const loadKalkulation = useCallback(async (id) => {
    let kal = savedKalkulationen.find(k => String(k.id) === String(id));

    if (!kal) {
      // Liste koennte veraltet sein -> frisch vom Server holen
      try {
        const resp = await apiFetch('/api/kalkulationen');
        if (!resp.ok) {
          setKalkulationenError(await errorFromResponse(resp, 'Kalkulationen konnten nicht geladen werden'));
          return false;
        }
        const data = await resp.json();
        const list = Array.isArray(data) ? data : [];
        setSavedKalkulationen(list);
        setKalkulationenError(null);
        kal = list.find(k => String(k.id) === String(id));
      } catch (err) {
        setKalkulationenError('Server nicht erreichbar: ' + err.message);
        return false;
      }
    }
    if (!kal) return false;

    const snapshot = kal.data || {};
    const form = snapshot.form || {};

    setFormData({
      plz: kal.plz ?? form.plz ?? '',
      verbrauch: String(kal.verbrauch ?? form.verbrauch ?? ''),
      zaehlverfahren: kal.zaehlverfahren || form.zaehlverfahren || 'SLP',
      leistung: kal.leistung != null ? String(kal.leistung) : (form.leistung || ''),
      lieferbeginn: (kal.lieferbeginn || form.lieferbeginn || '').slice(0, 10),
      branche: kal.branche || form.branche || '',
      spannungsebene: kal.spannungsebene || form.spannungsebene || 'NS',
      kurzbezeichnung: kal.kurzbezeichnung || form.kurzbezeichnung || '',
    });
    setProvision(Number(kal.provision) || 1.0);
    if (kal.price_mode === 'boerse' || kal.price_mode === 'versorger') setPriceMode(kal.price_mode);

    // Bewusst KEIN Wiedereinsetzen des alten Ergebnisses: die Preise werden
    // mit den aktuellen Boersendaten neu gerechnet, damit nichts Veraltetes
    // als aktuelles Angebot erscheint. Der Schnappschuss bleibt in `data`.
    setResult(null);
    setComparisonTable(null);
    setSelectedSuppliers(new Set());
    setCalcError(null);
    setStep(0);
    return true;
  }, [savedKalkulationen]);

  // Kalkulation loeschen (serverseitig)
  const deleteKalkulation = useCallback(async (id) => {
    setSaveError(null);
    try {
      const resp = await apiFetch(`/api/kalkulationen/${id}`, { method: 'DELETE' });
      if (!resp.ok) {
        setKalkulationenError(await errorFromResponse(resp, 'Kalkulation konnte nicht geloescht werden'));
        return false;
      }
      setSavedKalkulationen(prev => prev.filter(k => String(k.id) !== String(id)));
      setKalkulationenError(null);
      return true;
    } catch (err) {
      setKalkulationenError('Server nicht erreichbar: ' + err.message);
      return false;
    }
  }, []);

  // Zuruecksetzen
  const reset = useCallback(() => {
    setFormData(INITIAL_FORM);
    setStep(0);
    setProvision(1.0);
    setResult(null);
    setComparisonTable(null);
    setSelectedSuppliers(new Set());
    setCalcError(null);
    setSaveError(null);
  }, []);

  // Navigation
  const goToStep = useCallback((targetStep) => {
    if (targetStep >= 0 && targetStep <= 2) {
      setStep(targetStep);
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
    }
  }, []);

  return {
    // State
    formData,
    step,
    provision,
    result,
    comparisonTable,
    filteredResults,
    selectedSuppliers,
    savedKalkulationen,
    kalkulationenLoading,
    kalkulationenError,
    isCalculating,
    calcError,
    isSaving,
    saveError,
    suppliersLoading,
    priceMode,
    futures,
    futuresError,
    aufschlagStatus,
    netzentgelteStatus,

    // Actions
    updateForm,
    setPriceMode,
    calculate,
    updateProvision,
    toggleSupplier,
    saveKalkulation,
    loadKalkulation,
    deleteKalkulation,
    reloadKalkulationen: fetchKalkulationen,
    reloadAufschlag,
    reloadNetzentgelte,
    reset,
    goToStep,
    setStep,
  };
};
